import React, { useState, useEffect } from 'react';
import { loadBudgetOperations, loadCampaignTracking } from '../api/index';

const ComprehensiveDashboard = ({ 
  clients, 
  onSelectClient, 
  onNavigateToSection,
  currentUser,
  permissions 
}) => {
  const [dashboardData, setDashboardData] = useState({
    budgetOperations: [],
    campaignTracking: [],
    balanceReduction: [],
    noContactClients: [],
    noTrafficClients: [],
    excessJunkClients: [],
    cpcTrafficClients: [],
    conversionDrops: [],
    verifications: [],
    noAnswerClients: []
  });

  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30'); // días

  // Cargar datos de todas las secciones
  useEffect(() => {
    loadDashboardData();
  }, [selectedTimeframe]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Simular carga de datos de diferentes APIs
      const [
        budgetOps,
        campaigns,
        balanceRed,
        noContact,
        noTraffic,
        excessJunk,
        cpcTraffic,
        conversions,
        verifs,
        noAnswer
      ] = await Promise.allSettled([
        loadBudgetOperations(),
        loadCampaignTracking(),
        fetchBalanceReduction(),
        fetchNoContactClients(),
        fetchNoTrafficClients(),
        fetchExcessJunkClients(),
        fetchCpcTrafficClients(),
        fetchConversionDrops(),
        fetchVerifications(),
        fetchNoAnswerClients()
      ]);

      setDashboardData({
        budgetOperations: budgetOps.status === 'fulfilled' ? budgetOps.value : [],
        campaignTracking: campaigns.status === 'fulfilled' ? campaigns.value : [],
        balanceReduction: balanceRed.status === 'fulfilled' ? balanceRed.value : [],
        noContactClients: noContact.status === 'fulfilled' ? noContact.value : [],
        noTrafficClients: noTraffic.status === 'fulfilled' ? noTraffic.value : [],
        excessJunkClients: excessJunk.status === 'fulfilled' ? excessJunk.value : [],
        cpcTrafficClients: cpcTraffic.status === 'fulfilled' ? cpcTraffic.value : [],
        conversionDrops: conversions.status === 'fulfilled' ? conversions.value : [],
        verifications: verifs.status === 'fulfilled' ? verifs.value : [],
        noAnswerClients: noAnswer.status === 'fulfilled' ? noAnswer.value : []
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Funciones auxiliares para obtener datos de las APIs reales
  const fetchBalanceReduction = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/balance-reduction');
      return response.ok ? await response.json() : [];
    } catch {
      return [];
    }
  };

  const fetchNoContactClients = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/no-contact-clients');
      return response.ok ? await response.json() : [];
    } catch {
      return [];
    }
  };

  const fetchNoTrafficClients = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/no-traffic-clients');
      return response.ok ? await response.json() : [];
    } catch {
      return [];
    }
  };

  const fetchExcessJunkClients = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/excess-junk-clients');
      return response.ok ? await response.json() : [];
    } catch {
      return [];
    }
  };

  const fetchCpcTrafficClients = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/cpc-traffic-clients');
      return response.ok ? await response.json() : [];
    } catch {
      return [];
    }
  };

  const fetchConversionDrops = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/conversion-drops');
      return response.ok ? await response.json() : [];
    } catch {
      return [];
    }
  };

  const fetchVerifications = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/verifications');
      return response.ok ? await response.json() : [];
    } catch {
      return [];
    }
  };

  const fetchNoAnswerClients = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/no-answer-clients');
      return response.ok ? await response.json() : [];
    } catch {
      return [];
    }
  };

  // Calcular estadísticas generales
  const totalClients = clients.length;
  const totalMeetings = clients.reduce((total, client) => total + client.meetings.length, 0);
  const totalContacts = clients.reduce((total, client) => 
    total + client.meetings.reduce((sum, meeting) => sum + meeting.contactsGenerated, 0), 0
  );
  const totalPatients = clients.reduce((total, client) => 
    total + client.meetings.reduce((sum, meeting) => sum + meeting.patients, 0), 0
  );
  const conversionRate = totalContacts > 0 ? ((totalPatients / totalContacts) * 100).toFixed(1) : 0;

  // Estadísticas de operaciones
  const operationsStats = {
    budgetOperations: dashboardData.budgetOperations.length,
    campaignTracking: dashboardData.campaignTracking.length,
    balanceReduction: dashboardData.balanceReduction.length,
    totalBudgetIncrease: dashboardData.budgetOperations.reduce((sum, op) => sum + (op.amount || 0), 0),
    activeCampaigns: dashboardData.campaignTracking.filter(c => c.status === 'active').length,
    balanceReductionAmount: dashboardData.balanceReduction.reduce((sum, br) => sum + (br.amount || 0), 0)
  };

  // Estadísticas de problemas
  const problemsStats = {
    noContactClients: dashboardData.noContactClients.length,
    noTrafficClients: dashboardData.noTrafficClients.length,
    excessJunkClients: dashboardData.excessJunkClients.length,
    cpcTrafficClients: dashboardData.cpcTrafficClients.length,
    conversionDrops: dashboardData.conversionDrops.length,
    verifications: dashboardData.verifications.length,
    noAnswerClients: dashboardData.noAnswerClients.length,
    totalProblems: dashboardData.noContactClients.length + 
                   dashboardData.noTrafficClients.length + 
                   dashboardData.excessJunkClients.length + 
                   dashboardData.cpcTrafficClients.length + 
                   dashboardData.conversionDrops.length + 
                   dashboardData.verifications.length + 
                   dashboardData.noAnswerClients.length
  };

  const handleSectionClick = (sectionId) => {
    if (onNavigateToSection) {
      onNavigateToSection(sectionId);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="responsive-container responsive-padding">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="spinner mx-auto mb-4"></div>
              <p className="responsive-text-base text-gray-600">Cargando dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="responsive-container responsive-padding">
        {/* Header del Dashboard */}
        <div className="bg-gradient-to-r from-[#0061A8] to-[#004d8a] rounded-2xl shadow-xl responsive-padding mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
            <div className="text-white">
              <h1 className="responsive-text-2xl font-bold mb-2 tracking-tight">
                Dashboard Integral CRM
              </h1>
              <p className="text-blue-100 responsive-text-base">
                Resumen completo de operaciones y problemas del sistema
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                className="bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                <option value="7" className="text-gray-900">Últimos 7 días</option>
                <option value="30" className="text-gray-900">Últimos 30 días</option>
                <option value="90" className="text-gray-900">Últimos 90 días</option>
                <option value="365" className="text-gray-900">Último año</option>
              </select>
              
              <button
                onClick={loadDashboardData}
                className="bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-xl px-4 py-2 hover:bg-white/20 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas Principales */}
        <div className="responsive-grid-1-2-3-4 mb-8">
          <div className="card-premium border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="responsive-text-sm text-gray-600 font-medium">Total Clientes</p>
                <p className="responsive-text-xl font-bold text-blue-600 mt-1">{totalClients}</p>
                <p className="responsive-text-xs text-gray-500 mt-1">clientes registrados</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="card-premium border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="responsive-text-sm text-gray-600 font-medium">Total Juntas</p>
                <p className="responsive-text-xl font-bold text-green-600 mt-1">{totalMeetings}</p>
                <p className="responsive-text-xs text-gray-500 mt-1">reuniones realizadas</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="card-premium border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="responsive-text-sm text-gray-600 font-medium">Contactos</p>
                <p className="responsive-text-xl font-bold text-yellow-600 mt-1">{totalContacts.toLocaleString()}</p>
                <p className="responsive-text-xs text-gray-500 mt-1">leads generados</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="card-premium border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="responsive-text-sm text-gray-600 font-medium">Conversión</p>
                <p className="responsive-text-xl font-bold text-purple-600 mt-1">{conversionRate}%</p>
                <p className="responsive-text-xs text-gray-500 mt-1">{totalPatients} pacientes</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de Operaciones */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="responsive-text-lg font-bold text-gray-900">📊 Operaciones</h2>
            <span className="responsive-text-sm text-gray-500">
              {operationsStats.budgetOperations + operationsStats.campaignTracking + operationsStats.balanceReduction} operaciones activas
            </span>
          </div>

          <div className="responsive-grid-1-2-3-4 mb-6">
            <button
              onClick={() => handleSectionClick('budgetOperations')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Aumento de Ppto Google</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="responsive-text-sm text-gray-600">Operaciones</span>
                  <span className="responsive-text-sm font-bold text-blue-600">{operationsStats.budgetOperations}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="responsive-text-sm text-gray-600">Total Incremento</span>
                  <span className="responsive-text-sm font-bold text-green-600">${operationsStats.totalBudgetIncrease.toLocaleString()}</span>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleSectionClick('campaignTracking')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Seguimiento de Campañas</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="responsive-text-sm text-gray-600">Total Campañas</span>
                  <span className="responsive-text-sm font-bold text-green-600">{operationsStats.campaignTracking}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="responsive-text-sm text-gray-600">Activas</span>
                  <span className="responsive-text-sm font-bold text-blue-600">{operationsStats.activeCampaigns}</span>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleSectionClick('balanceReduction')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Clientes que Bajan Saldo</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="responsive-text-sm text-gray-600">Casos</span>
                  <span className="responsive-text-sm font-bold text-red-600">{operationsStats.balanceReduction}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="responsive-text-sm text-gray-600">Monto Total</span>
                  <span className="responsive-text-sm font-bold text-red-600">${operationsStats.balanceReductionAmount.toLocaleString()}</span>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Sección de Problemas */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="responsive-text-lg font-bold text-gray-900">⚠️ Problemas</h2>
            <span className="responsive-text-sm text-gray-500">
              {problemsStats.totalProblems} problemas detectados
            </span>
          </div>

          <div className="responsive-grid-1-2-3-4 mb-6">
            <button
              onClick={() => handleSectionClick('noContactClients')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L12 12m6.364 6.364L12 12m0 0L5.636 5.636M12 12l6.364 6.364M12 12L5.636 5.636" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-orange-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Bajaron o Sin Contactos</h3>
              <div className="text-center">
                <span className="responsive-text-xl font-bold text-orange-600">{problemsStats.noContactClients}</span>
                <p className="responsive-text-xs text-gray-500">clientes afectados</p>
              </div>
            </button>

            <button
              onClick={() => handleSectionClick('noTrafficClients')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Sin Tráfico</h3>
              <div className="text-center">
                <span className="responsive-text-xl font-bold text-red-600">{problemsStats.noTrafficClients}</span>
                <p className="responsive-text-xs text-gray-500">clientes afectados</p>
              </div>
            </button>

            <button
              onClick={() => handleSectionClick('excessJunkClients')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-yellow-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Exceso de Basura</h3>
              <div className="text-center">
                <span className="responsive-text-xl font-bold text-yellow-600">{problemsStats.excessJunkClients}</span>
                <p className="responsive-text-xs text-gray-500">clientes afectados</p>
              </div>
            </button>

            <button
              onClick={() => handleSectionClick('cpcTrafficClients')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Aumento CPC y ↓ Tráfico</h3>
              <div className="text-center">
                <span className="responsive-text-xl font-bold text-purple-600">{problemsStats.cpcTrafficClients}</span>
                <p className="responsive-text-xs text-gray-500">clientes afectados</p>
              </div>
            </button>
          </div>

          {/* Segunda fila de problemas */}
          <div className="responsive-grid-1-2-3-4">
            <button
              onClick={() => handleSectionClick('conversionDrops')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-pink-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Conversiones Caídas</h3>
              <div className="text-center">
                <span className="responsive-text-xl font-bold text-pink-600">{problemsStats.conversionDrops}</span>
                <p className="responsive-text-xs text-gray-500">clientes afectados</p>
              </div>
            </button>

            <button
              onClick={() => handleSectionClick('verifications')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Verificaciones</h3>
              <div className="text-center">
                <span className="responsive-text-xl font-bold text-indigo-600">{problemsStats.verifications}</span>
                <p className="responsive-text-xs text-gray-500">verificaciones pendientes</p>
              </div>
            </button>

            <button
              onClick={() => handleSectionClick('noAnswerClients')}
              className="card-premium hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-left group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="responsive-text-base font-semibold text-gray-900 mb-2">Clientes que No Contestan</h3>
              <div className="text-center">
                <span className="responsive-text-xl font-bold text-gray-600">{problemsStats.noAnswerClients}</span>
                <p className="responsive-text-xs text-gray-500">clientes afectados</p>
              </div>
            </button>
          </div>
        </div>

        {/* Resumen de Actividad Reciente */}
        <div className="card-premium mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="responsive-text-lg font-bold text-gray-900">📈 Resumen de Actividad</h2>
            <button
              onClick={loadDashboardData}
              className="responsive-text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Actualizar datos
            </button>
          </div>

          <div className="responsive-grid-1-2-3-4">
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
              <div className="text-2xl mb-2">📊</div>
              <h3 className="responsive-text-sm font-semibold text-gray-900 mb-1">Operaciones Totales</h3>
              <p className="responsive-text-xl font-bold text-blue-600">
                {operationsStats.budgetOperations + operationsStats.campaignTracking + operationsStats.balanceReduction}
              </p>
            </div>

            <div className="text-center p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-xl">
              <div className="text-2xl mb-2">⚠️</div>
              <h3 className="responsive-text-sm font-semibold text-gray-900 mb-1">Problemas Detectados</h3>
              <p className="responsive-text-xl font-bold text-red-600">{problemsStats.totalProblems}</p>
            </div>

            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
              <div className="text-2xl mb-2">💰</div>
              <h3 className="responsive-text-sm font-semibold text-gray-900 mb-1">Incremento Total</h3>
              <p className="responsive-text-xl font-bold text-green-600">
                ${operationsStats.totalBudgetIncrease.toLocaleString()}
              </p>
            </div>

            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
              <div className="text-2xl mb-2">🎯</div>
              <h3 className="responsive-text-sm font-semibold text-gray-900 mb-1">Tasa de Conversión</h3>
              <p className="responsive-text-xl font-bold text-purple-600">{conversionRate}%</p>
            </div>
          </div>
        </div>

        {/* Clientes Recientes */}
        <div className="card-premium">
          <div className="flex items-center justify-between mb-6">
            <h2 className="responsive-text-lg font-bold text-gray-900">👥 Clientes Recientes</h2>
            <button
              onClick={() => handleSectionClick('clients')}
              className="responsive-text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Ver todos los clientes
            </button>
          </div>

          <div className="space-y-4">
            {clients.slice(0, 5).map((client) => (
              <button
                key={client.id}
                onClick={() => onSelectClient && onSelectClient(client)}
                className="w-full flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-all duration-200 cursor-pointer group"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <span className="text-white font-bold responsive-text-sm">
                      {client.name.charAt(0)}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="responsive-text-sm font-semibold text-gray-900 truncate group-hover:text-blue-900">
                    {client.name}
                  </p>
                  <p className="responsive-text-xs text-gray-500 group-hover:text-blue-600">
                    {client.specialty} • {client.cell || 'Sin célula'}
                  </p>
                  <p className="responsive-text-xs text-gray-400">
                    {client.country || 'Sin país especificado'}
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-center space-x-3">
                  <div className="text-center">
                    <p className="responsive-text-xs text-gray-500">Juntas</p>
                    <p className="responsive-text-sm font-bold text-green-600">{client.meetings.length}</p>
                  </div>
                  <div className="text-center">
                    <p className="responsive-text-xs text-gray-500">Contactos</p>
                    <p className="responsive-text-sm font-bold text-yellow-600">
                      {client.meetings.reduce((sum, meeting) => sum + meeting.contactsGenerated, 0)}
                    </p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>

          {clients.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="responsive-text-base text-gray-500 font-medium">No hay clientes registrados</p>
              <p className="responsive-text-sm text-gray-400 mt-2">Los clientes aparecerán aquí una vez que sean agregados al sistema</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveDashboard;
